import { GoogleLogin } from '@react-oauth/google';
import { ArrowLeft, ArrowRight, Lock, Mail, ShieldCheck, TrendingUp, User } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { trackEvent } from "../../utils/analytics";

// Google OAuth is only available when a real client ID is configured
const GOOGLE_CONFIGURED =
  process.env.REACT_APP_GOOGLE_CLIENT_ID &&
  process.env.REACT_APP_GOOGLE_CLIENT_ID !== "dummy_id";

const LoginForm = () => {
  const [mode, setMode] = useState("login"); // login, register, forgot, mfa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [totp, setTotp] = useState("");
  const [pendingMfaToken, setPendingMfaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const { login, register, googleLogin, completeMfaLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      if (mode === "forgot") {
         const { data } = await api.post("/auth/forgot-password", { email });
         setSuccessMsg(data.message);
         setLoading(false);
         return;
      }

      if (mode === "mfa") {
        // Verify TOTP using the pre-MFA token stored in state
        const { data } = await api.post(
          "/auth/2fa/verify-login",
          { totp },
          { headers: { Authorization: `Bearer ${pendingMfaToken}` } }
        );
        if (data.success) {
          await completeMfaLogin(pendingMfaToken);
          trackEvent("user_logged_in", { method: "email_mfa" });
          navigate("/");
        } else {
          setError(data.error || "Invalid code");
        }
        setLoading(false);
        return;
      }

      let result;
      if (mode === "login") {
        result = await login(email, password);
      } else {
        result = await register({ email, password, firstName, lastName });
      }

      if (result.requiresMfa) {
        // Switch to MFA verification step
        setPendingMfaToken(result.mfaToken);
        setMode("mfa");
        setLoading(false);
        return;
      }

      if (result.success) {
        trackEvent(mode === "login" ? "user_logged_in" : "user_registered", { method: "email" });
        navigate("/");
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.response?.data?.error || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
      setLoading(true);
      setError("");
      try {
          const result = await googleLogin(credentialResponse.credential);
          if (result.success) {
              trackEvent("user_logged_in", { method: "google" });
              navigate("/");
          } else {
              setError(result.error);
          }
      } catch (err) {
          setError("Google Authentication failed");
      } finally {
          setLoading(false);
      }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-900 to-black relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
      <div className="absolute top-0 -right-4 w-72 h-72 bg-emerald-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
      <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>

      <div className="relative w-full max-w-md p-8 mx-4">
        {/* Glass Card */}
        <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl p-8">
          
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="p-3 bg-emerald-500/10 rounded-xl">
                <TrendingUp size={32} className="text-emerald-400" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Trading Journal
            </h1>
            <p className="text-slate-400">
              {mode === "login" && "Welcome back, trader"}
              {mode === "register" && "Start your profitable journey"}
              {mode === "forgot" && "Reset your password"}
              {mode === "mfa" && "Two-factor authentication"}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm text-center">
              {error}
            </div>
          )}

          {successMsg && (
            <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400 text-sm text-center">
              {successMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "mfa" && (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 mb-4 text-slate-400">
                  <ShieldCheck size={20} className="text-emerald-400" />
                  <span className="text-sm">Enter the 6-digit code from your authenticator app</span>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-400 ml-1">Authentication Code</label>
                  <div className="relative">
                    <ShieldCheck size={16} className="absolute left-3 top-3 text-slate-500" />
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pl-10 pr-4 text-slate-200 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all text-sm tracking-widest text-center"
                      placeholder="000000"
                      value={totp}
                      onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      autoFocus
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {mode !== "mfa" && mode === "register" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-400 ml-1">First Name</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-3 text-slate-500" />
                    <input
                      type="text"
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pl-10 pr-4 text-slate-200 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all text-sm"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required={mode === "register"}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-400 ml-1">Last Name</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-3 text-slate-500" />
                    <input
                      type="text"
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pl-10 pr-4 text-slate-200 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all text-sm"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required={mode === "register"}
                    />
                  </div>
                </div>
              </div>
            )}

            {mode !== "mfa" && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-400 ml-1">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-3 text-slate-500" />
                <input
                  type="email"
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pl-10 pr-4 text-slate-200 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all text-sm"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            )}

            {mode !== "forgot" && mode !== "mfa" && (
              <div className="space-y-1">
                <div className="flex items-center justify-between ml-1">
                  <label className="text-xs font-medium text-slate-400">Password</label>
                  {mode === "login" && (
                    <button 
                      type="button" 
                      onClick={() => setMode("forgot")}
                      className="text-xs text-emerald-400 hover:text-emerald-300"
                    >
                      Forgot?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-3 text-slate-500" />
                  <input
                    type="password"
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pl-10 pr-4 text-slate-200 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all text-sm"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white font-bold py-3 rounded-xl shadow-lg shadow-emerald-500/20 transition-all hover:scale-[1.02] flex items-center justify-center gap-2 mt-6"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {mode === "login" && "Sign In"}
                  {mode === "register" && "Create Account"}
                  {mode === "forgot" && "Send Reset Link"}
                  {mode === "mfa" && "Verify Code"}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
            
            {mode !== "forgot" && mode !== "mfa" && GOOGLE_CONFIGURED && (
              <div className="mt-6 flex flex-col items-center gap-4 border-t border-slate-700/50 pt-6">
                <span className="text-xs text-slate-500">OR CONTINUE WITH</span>
                <div className="w-full flex justify-center brightness-110">
                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setError("Google login failed. Please try again or use email/password.")}
                        theme="filled_black"
                        shape="circle"
                        width="100%"
                    />
                </div>
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            {mode !== "login" ? (
               <button
                 onClick={() => { setMode("login"); setError(""); setSuccessMsg(""); setPendingMfaToken(null); setTotp(""); }}
                 className="flex items-center justify-center gap-2 w-full text-slate-400 hover:text-emerald-400 text-sm font-medium transition-colors"
               >
                 <ArrowLeft size={16} />
                 Back to Login
               </button>
            ) : (
               <button
                 onClick={() => setMode("register")}
                 className="text-slate-400 hover:text-emerald-400 text-sm font-medium transition-colors"
               >
                 Don't have an account? Sign up
               </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="absolute bottom-0 left-0 right-0 py-6">
        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-1 text-xs text-slate-600">
            <span>Not financial advice.</span>
            <span>Trading involves substantial risk of loss.</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-600">
            <Link to="/privacy" className="hover:text-slate-400 transition-colors">Privacy Policy</Link>
            <span className="text-slate-700">|</span>
            <Link to="/terms" className="hover:text-slate-400 transition-colors">Terms of Service</Link>
            <span className="text-slate-700">|</span>
            <Link to="/disclaimer" className="hover:text-slate-400 transition-colors">Disclaimer</Link>
          </div>
          <p className="text-xs text-slate-700">&copy; {new Date().getFullYear()} Trading Journal. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LoginForm;
